import { useMutation } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ResetReqFormData } from "@/modules/Auth/types/resetReqFormData";
import { resetReqFn } from "@/modules/Auth/mutations/resetReqFn";
import { useState } from "react";
import useAccountMessages from "@/utils/useAccountMessages";
import { toast } from "@/client/hooks/use-toast";
import useAuthValidationSchema from "@/modules/Auth/schemas/useAuthValidationSchema";

export const useForget = () => {
    const [isReqSuccess, setIsReqSuccess] = useState(false);
    const { resetReqSchema } = useAuthValidationSchema();
    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<ResetReqFormData>({
        resolver: zodResolver(resetReqSchema),
        mode: "onBlur",
    });
    const [usedEmail, setUsedEmail] = useState("");
    const { getValMsg } = useAccountMessages();

    const mutation = useMutation({
        mutationFn: resetReqFn,
        retry: 1,
        onSuccess: (response) => {
            if (response.error) {
                setError("root", {
                    message: response.error.message,
                });
                toast({
                    title: getValMsg(response.error.messageKey, response.error.message),
                    variant: "destructive",
                });
                return;
            }

            setIsReqSuccess(true);
            toast({
                title: getValMsg("password-reset-instructions-sent"),
                variant: "success",
            });
        },
    });

    const onSubmit: SubmitHandler<ResetReqFormData> = async (data: ResetReqFormData) => {
        setUsedEmail(data.email);
        mutation.mutate(data);
    };

    const resetMutation = () => {
        mutation.reset();
    }

    const pending = mutation.isPending || isSubmitting;
    return { register, handleSubmit, onSubmit, errors, pending, isReqSuccess, usedEmail ,resetMutation};
};
